import _ from 'lodash';
import log from '$$log';
import Renderer from './render';
import DataProcessor from './data';
import Filter from './filter';
import constants from '$$helpers/constants';
import ganttPreloader from '../../helpers/preloader';
import { reportWorkerError } from '$$workerErrorReporting';

class Core {
  constructor() {
    this.ganttInstance = null;
    this.workloadStore = null;
    this.workloadWorker = null;
    this.cachedWorkerData = {};
    this.cachedParseData = null;
    this.cacheMap = {};
  }

  init(instance, config) {
    this.config = {
      RESOURCE_TASK_ID_POSTFIX: '-tasks',
      ROW_HEIGHT: 30,
      RESOURCE_TASK_HEIGHT: 22,
      RESOURCE_TASK_SMALL_HEIGHT: 4,
      TASK_BAR_TOP_POSITION: 4,
      PARTIAL_ROWS_COUNT: 20,
      resourcesLayout: {},
      openedResources: {},
      selectedTask: {},
      countOfUnassignedTaskRows: 20,
      taskCellsEditMode: {},
      showPreloader: true,
      ...config,
    };

    this.workloadStore = instance.createDatastore({
      name: 'workloadStore',
    });

    const tDataProcessor = log.time('ganttRender.workload.core.dataProcessor');

    DataProcessor.init(instance, this.config);

    tDataProcessor.end();

    const tRenderer = log.time('ganttRender.workload.core.renderer');

    Renderer.init(instance, this.config);

    tRenderer.end();

    const tFilter = log.time('ganttRender.workload.core.filter');

    Filter.init(instance, this.config);

    tFilter.end();

    this.ganttInstance = instance;

    const tWorkers = log.time('ganttRender.workload.core.initWorkers');

    this.initWorkers();

    tWorkers.end();

    const tClearCache = log.time('ganttRender.workload.core.clearCache');

    this.clearCache();

    tClearCache.end();

    this.calculateAndRender();
  }

  async calculateAndRender(needResize = true) {
    const tCalc = log.time('ganttRender.workload.core.calculateAndRender');

    if (!this._validateCalculateAndRender()) {
      tCalc.end();

      return;
    }

    if (this.config.showPreloader) {
      ganttPreloader.show();
      this.config.showPreloader = false;
    }

    const tPrepare = log.time('ganttRender.workload.core.calc.prepareData');

    let dataForParse = [];
    const preparedData = DataProcessor.prepareData();

    tPrepare.end();

    // if (!preparedData.length) {
    //   return;
    // }

    const tCache = log.time('ganttRender.workload.core.calc.applyCache');

    const cached = DataProcessor.applyCacheToData(preparedData, this.cachedWorkerData);

    tCache.end();

    if (cached.needReCalculate) {
      const tWorker = log.time('ganttRender.workload.core.calc.worker');

      const workerConfig = DataProcessor.prepareWorkerConfig();

      const workerData = {
        innerData: cached.data,
        ...workerConfig,
      };

      const calculatedData = await this.workloadWorker(_.cloneDeep(workerData));

      this._updateCacheData(calculatedData);

      this.config.resourcesLayout = calculatedData.resourcesLayout;
      dataForParse = calculatedData.resourcesData;

      tWorker.end();
    } else {
      dataForParse = cached.data;
    }

    const tSmartScroll = log.time('ganttRender.workload.core.calc.prepareSmartScroll');

    dataForParse = DataProcessor.prepareDataForSmartScroll(dataForParse);

    tSmartScroll.end();

    const tBeforeRender = log.time('ganttRender.workload.core.calc.beforeRender');

    Renderer.beforeRender();

    this.cachedParseData = dataForParse;

    this.workloadStore.clearAll();
    this.workloadStore.parse(dataForParse);

    tBeforeRender.end();

    const tAfterRender = log.time('ganttRender.workload.core.calc.afterRender');

    Renderer.afterRender(!!dataForParse.length, needResize);

    tAfterRender.end();

    this.lastTimelineStart = this.ganttInstance.config.start_date;
    this.lastTimelineEnd = this.ganttInstance.config.end_date;

    ganttPreloader.hide();
    tCalc.end();
  }

  _validateCalculateAndRender() {
    if (this.config.type === constants.WORKLOAD_TYPES.panel) {
      this.config.showPreloader = false;

      if (this.ganttInstance.config.hide_workload) {
        return;
      }

      if (this.ganttInstance.config.durationData.mode === 'hour') {
        return;
      }
    }

    if (this.ganttInstance.$destroyed) {
      return;
    }

    return true;
  }

  refresh() {
    if (this.cachedParseData) {
      const prepareData = DataProcessor.prepareDataForSmartScroll(this.cachedParseData);

      Renderer.beforeRender();

      this.workloadStore.clearAll();
      this.workloadStore.parse(prepareData);

      Renderer.afterRender(!!prepareData.length);
    }
  }

  clearCache(updatedData) {
    if (updatedData) {
      if (updatedData.taskIds) {
        updatedData.taskIds.forEach(taskId => {
          if (this.cacheMap[taskId]) {
            Object.keys(this.cacheMap[taskId]).forEach(resourceDataId => {
              this.clearCachedWorkerDataById(resourceDataId);
            });
          }
        });
        // TODO think about optimization unassigned section
        this.clearCachedWorkerDataById('unassigned');
        this.clearCachedWorkerDataById('unassigned-tasks');
      }
      if (updatedData.resourceIds) {
        updatedData.resourceIds.forEach(resourceId => {
          this.clearCachedWorkerDataById(resourceId);
          this.clearCachedWorkerDataById(`${resourceId}-tasks`);
        });
      }

      if (!updatedData.taskIds && !updatedData.resourceIds) {
        this.cachedWorkerData = {};
      }
    } else {
      this.cachedWorkerData = {};
    }

    this.cachedParseData = null;
  }

  clearCachedWorkerDataById(id) {
    if (this.cachedWorkerData[id]) {
      this.cachedWorkerData[id] = null;
    }
  }

  checkIsUpdatedTimeline() {
    if (!Object.keys(this.ganttInstance.$data.tasksStore.pull).length) {
      return;
    }
    if (!this.lastTimelineStart && !this.lastTimelineEnd) {
      return;
    }
    if (this.ganttInstance.config.start_date?.valueOf() !== this.lastTimelineStart.valueOf()
      || this.ganttInstance.config.end_date?.valueOf() !== this.lastTimelineEnd.valueOf()) {
      this.clearCache();

      return true;
    }
  }

  render() {
    Renderer.refresh();
  }

  changeCalculationMode(value) {
    this.config.calculationMode = value;
    this.clearCache();
    this.calculateAndRender(false);
  }

  changeZoom(zoom) {
    if (!this.config) {
      return;
    }

    this.config.zoom = zoom;
    this.config.openedResources = {};
    this.config.selectedTask = {};
    this.clearCache();
    this.calculateAndRender();
  }

  changeViewMode(value) {
    this.config.viewMode = value;
    this.config.selectedTask = {};
    this.clearCache();
    this.calculateAndRender();
  }

  initWorkers() {
    let version = Date.now();

    if (GT.productVersion && GT.productVersion.buildVersion) {
      version = GT.productVersion.buildVersion;
    }

    if (!this.workloadWorker) {
      this.workloadWorker = this._createWorker(`/workers/workload_worker.js?${version}`);
    }
  }

  _createWorker(workerPath) {
    const worker = new Worker(workerPath);

    return function exec(workerData) {
      return new Promise((resolve, reject) => {
        worker.onmessage = e => {
          worker.onmessage = null;
          worker.onerror = null;

          return resolve(e.data);
        };

        worker.onerror = e => {
          worker.onmessage = null;
          worker.onerror = null;
          reportWorkerError(e, 'workload_worker');

          return reject(e);
        };

        worker.postMessage(workerData);
      });
    };
  }

  _updateCacheData(calculatedData) {
    this.cacheMap = {};

    calculatedData.resourcesData.forEach(resourceData => {
      this.cachedWorkerData[resourceData.id] = resourceData;

      resourceData.tasksByGanttId.forEach(data => {
        if (!this.cacheMap[data.task_id]) {
          this.cacheMap[data.task_id] = { [resourceData.id]: true };
        } else {
          this.cacheMap[data.task_id][resourceData.id] = true;
        }
      });
    });
  }

  isWorkloadPanelType() {
    return this.config.type === constants.WORKLOAD_TYPES.panel;
  }
}

export default new Core();
